// ./src/utils/api/CarTotalExpenseService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Car } from "./CarService";
import { ExpenseKind } from "./AppEssentialsService";

// GraphQL queries
const CAR_TOTAL_EXPENSE_LIST_QUERY = gql`
  query CarTotalExpenseList(
    $filter: CarTotalExpenseFilter
    $params: PaginationAndSorting
  ) {
    carTotalExpenseList(filter: $filter, params: $params) {
      code
      data {
        carId
        homeCurrency
        expenseKindId
        recordsCount
        amount
        expenseKind {
          id
          code
          canSchedule
          isItMaintenance
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CAR_TOTAL_EXPENSE_GET_QUERY = gql`
  query CarTotalExpenseGet(
    $carId: ID
    $homeCurrency: String
    $expenseKindId: Int
  ) {
    carTotalExpenseGet(
      carId: $carId
      homeCurrency: $homeCurrency
      expenseKindId: $expenseKindId
    ) {
      code
      data {
        carId
        homeCurrency
        expenseKindId
        recordsCount
        amount
        expenseKind {
          id
          code
          canSchedule
          isItMaintenance
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface CarTotalExpense {
  carId?: string | undefined;
  homeCurrency?: string | undefined;
  expenseKindId?: number | undefined;
  recordsCount?: number | undefined;
  amount?: number | undefined;
  car?: Car | undefined;
  expenseKind?: ExpenseKind | undefined;
}

export interface CarTotalExpenseFilter {
  carId?: string[] | null | undefined;
  homeCurrency?: string[] | null | undefined;
  expenseKindId?: number[] | null | undefined;
}

export interface CarTotalExpenseWhereInput {
  carId?: string | undefined;
  homeCurrency?: string | undefined;
  expenseKindId?: number | undefined;
}

/**
 * CarTotalExpense service that handles car total expense-related API calls.
 * This is a read-only service for viewing aggregated total expense data by car, currency, and expense kind.
 * Supports: list, get (triple composite key)
 */
export class CarTotalExpenseService {
  /**
   * Get a list of car total expenses with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: CarTotalExpenseFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(CAR_TOTAL_EXPENSE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single car total expense by composite key (carId + homeCurrency + expenseKindId)
   */
  static async get(
    carId: string,
    homeCurrency: string,
    expenseKindId: number
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(CAR_TOTAL_EXPENSE_GET_QUERY, {
      carId,
      homeCurrency,
      expenseKindId,
    });
    return result;
  }
}
